import { useEffect, useRef } from "react";
import { useAuth, useUser } from "@clerk/react";
import { customFetch } from "@workspace/api-client-react";
import { analyticsAllowed } from "@workspace/observability";
import {
  forgetReferral,
  getCapturedReferral,
  pickReferral,
  readStoredReferral,
  sanitizeReferral,
  storeReferral,
  type Referral,
} from "@/lib/referral";

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Boot hook (main.tsx): keep this visit's referral for 30 days, consent permitting. */
export function persistReferralIfConsented(): void {
  const s = storage();
  if (s && analyticsAllowed()) storeReferral(s, getCapturedReferral());
}

/** The referral to attach to a new sign-up (Clerk unsafeMetadata). */
export function referralForSignUp(): Referral | null {
  const s = storage();
  return pickReferral(getCapturedReferral(), s ? readStoredReferral(s) : null);
}

/**
 * Once signed in, hand the sign-up's referral (this visit, the 30-day local
 * copy, or what Clerk carried through sign-up as `unsafeMetadata.referral`)
 * to POST /api/me/referral. The server keeps it write-once and only for a
 * just-created account, so sending it on every sign-in is harmless; the local
 * copy is forgotten after any definite answer.
 */
export function ReferralClaimer() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const sentFor = useRef<string | null>(null);

  useEffect(() => {
    persistReferralIfConsented();
    if (!isLoaded || !isSignedIn || !user || sentFor.current === user.id) return;
    const s = storage();
    const fromClerk = sanitizeReferral(
      (user.unsafeMetadata as Record<string, unknown> | undefined)?.["referral"],
    );
    const referral = pickReferral(getCapturedReferral(), s ? readStoredReferral(s) : null, fromClerk);
    if (!referral) return;
    sentFor.current = user.id;
    void customFetch("/api/me/referral", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(referral),
    })
      .then(() => {
        if (s) forgetReferral(s);
      })
      .catch(() => {
        // Best effort; retried on the next load while the account is < 24h old.
        sentFor.current = null;
      });
  }, [isLoaded, isSignedIn, user]);

  return null;
}
